import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useReadContract, useWriteContract, usePublicClient } from "wagmi";
import { formatEther, type Address } from "viem";
import { ERC721DropAbi } from "@atelier/abis";
import { api } from "@/lib/api";
import { parseAllowlistBulk } from "./allowlistParser";

type AllowlistEntry = {
  id: string;
  wallet: Address;
  maxQty: number;
  priceWei: string;
};

type DropInfo = {
  signerAddress: Address;
  publicMintAt: number | null;
  authorizedSigner: Address | null;
  allowlistCount: number;
};

/**
 * Inline manager rendered inside a Drop row in the dashboard.
 *
 * Two-step activation:
 *   1. Owner calls Drop.setSigner(workerSignerAddress) once. This authorizes
 *      the Atelier worker to issue EIP-712 AllowlistAuth signatures.
 *   2. Owner uploads wallets (one per line: "0xabc, 5, 0.01") and the worker
 *      starts handing out signatures for those wallets only.
 *
 * If the on-chain authorizedSigner doesn't match the worker's, the activation
 * step is shown front-and-center.
 */
export function AllowlistManager({
  contractAddress,
  chainId,
}: {
  contractAddress: Address;
  chainId: number;
}) {
  const qc = useQueryClient();
  const pc = usePublicClient({ chainId });
  const { writeContractAsync, isPending } = useWriteContract();

  // Worker info (signer address + on-chain config snapshot from D1).
  const { data: info, refetch: refetchInfo } = useQuery({
    queryKey: ["drop-info", chainId, contractAddress],
    queryFn: () =>
      api.get<DropInfo>(`/api/drops/${chainId}/${contractAddress}/info`),
  });

  // On-chain signer is the source of truth for "is allowlist live?".
  const { data: onChainSigner, refetch: refetchSigner } = useReadContract({
    address: contractAddress,
    abi: ERC721DropAbi,
    functionName: "authorizedSigner",
    chainId,
  });

  const signerActive =
    !!info && !!onChainSigner &&
    (onChainSigner as string).toLowerCase() === info.signerAddress.toLowerCase();

  const { data: list, refetch: refetchList } = useQuery({
    queryKey: ["drop-allowlist", chainId, contractAddress],
    queryFn: () =>
      api.get<{ entries: AllowlistEntry[] }>(
        `/api/drops/${chainId}/${contractAddress}/allowlist`,
        { auth: true },
      ),
    enabled: signerActive,
  });

  const [bulkText, setBulkText] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  const enableSigner = async () => {
    if (!info) return;
    setBusy(true);
    setStatus("Confirm in wallet…");
    try {
      const hash = await writeContractAsync({
        address: contractAddress,
        abi: ERC721DropAbi,
        functionName: "setSigner",
        args: [info.signerAddress],
        chainId,
      });
      setStatus("Confirming on-chain…");
      if (pc) await pc.waitForTransactionReceipt({ hash });
      await refetchSigner();
      await refetchInfo();
      setStatus("Allowlist enabled — start adding wallets below.");
    } catch (err) {
      setStatus(`Error: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  const parsed = useMemo(() => parseAllowlistBulk(bulkText), [bulkText]);

  const upload = async () => {
    if (parsed.entries.length === 0) return;
    setBusy(true);
    setStatus(`Uploading ${parsed.entries.length} entries…`);
    try {
      const resp = await api.post<{ added: number; updated: number }>(
        `/api/drops/${chainId}/${contractAddress}/allowlist`,
        { entries: parsed.entries },
        { auth: true },
      );
      setBulkText("");
      setStatus(`${resp.added} added · ${resp.updated} updated.`);
      await refetchList();
      await qc.invalidateQueries({ queryKey: ["drop-info", chainId, contractAddress] });
    } catch (err) {
      setStatus(`Error: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  const removeEntry = async (wallet: Address) => {
    setBusy(true);
    setStatus(`Removing ${wallet.slice(0, 6)}…`);
    try {
      await api.del(
        `/api/drops/${chainId}/${contractAddress}/allowlist/${wallet}`,
        { auth: true },
      );
      await refetchList();
      setStatus(null);
    } catch (err) {
      setStatus(`Error: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3 rounded-md border border-border bg-muted/30 p-3 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold">Allowlist</span>
        {info?.publicMintAt ? (
          <span className="text-muted-foreground">
            presale until {new Date(info.publicMintAt * 1000).toLocaleString()}
          </span>
        ) : (
          <span className="text-muted-foreground">no presale window configured</span>
        )}
      </div>

      {!signerActive ? (
        <div className="flex flex-col gap-2">
          <p className="text-muted-foreground">
            Authorize the Atelier worker to issue allowlist signatures. One on-chain transaction.
            Worker signer:{" "}
            {info?.signerAddress ? (
              <code className="text-foreground">
                {info.signerAddress.slice(0, 8)}…{info.signerAddress.slice(-6)}
              </code>
            ) : (
              "…"
            )}
          </p>
          <button
            className="btn-primary h-8 self-start px-3 text-xs"
            disabled={busy || isPending || !info}
            onClick={() => void enableSigner()}
          >
            {busy || isPending ? "Working…" : "Enable allowlist"}
          </button>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            <label className="flex flex-col gap-1">
              <span className="text-muted-foreground">
                Add wallets — one per line: <code>0xabc…, maxQty, priceETH (optional)</code>
              </span>
              <textarea
                className="input min-h-[80px] font-mono text-[11px]"
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                placeholder={"0x1234…, 2, 0.005\n0x5678…, 1\n0x9abc…, 5, 0.01"}
                disabled={busy}
              />
            </label>
            {parsed.errors.length > 0 && (
              <div className="text-amber-700 dark:text-amber-400">
                {parsed.errors.length} line(s) skipped:
                <ul className="list-disc pl-4">
                  {parsed.errors.slice(0, 3).map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              </div>
            )}
            <button
              className="btn-secondary h-8 px-3 text-xs"
              disabled={busy || parsed.entries.length === 0}
              onClick={() => void upload()}
            >
              Add {parsed.entries.length} {parsed.entries.length === 1 ? "wallet" : "wallets"}
            </button>
          </div>

          {list && list.entries.length > 0 && (
            <div className="space-y-1">
              <div className="text-muted-foreground">
                {list.entries.length} on the list
              </div>
              <ul className="max-h-48 space-y-1 overflow-y-auto">
                {list.entries.map((e) => (
                  <li
                    key={e.id}
                    className="flex items-center gap-2 rounded border border-border bg-background px-2 py-1"
                  >
                    <code className="flex-1 truncate font-mono text-[11px]">{e.wallet}</code>
                    <span className="tabular-nums text-muted-foreground">
                      {e.maxQty} × {formatEther(BigInt(e.priceWei))}
                    </span>
                    <button
                      className="btn-ghost h-6 px-2 text-xs"
                      disabled={busy}
                      onClick={() => void removeEntry(e.wallet)}
                      aria-label={`Remove ${e.wallet}`}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {status && <div className="text-muted-foreground">{status}</div>}
    </div>
  );
}
